import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getMediaById, getRelatedMedia } from "@/lib/data";
import { typeLabels, typePaths } from "@/lib/labels";
import { useSeo } from "@/hooks/useSeo";
import MediaCard from "@/components/feature/MediaCard";
import PlayModal from "@/components/feature/PlayModal";
import SectionHeading from "@/components/feature/SectionHeading";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;
  const [playOpen, setPlayOpen] = useState(false);

  useSeo(
    item ? `${item.title} - 榴莲视频` : "影视详情 - 榴莲视频",
    item ? item.synopsis.slice(0, 80) : "榴莲视频影视详情页",
    item ? `榴莲视频,${item.title},${item.genres.join(",")},在线观看` : "榴莲视频,影视详情",
  );

  if (!item) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-200/70 text-foreground-300">
          <i className="ri-film-line text-4xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-400">链接可能已失效，或影片已下架</p>
        <Link
          to="/"
          className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMedia(item, 6);
  const meta = [
    { label: "类型", value: item.genres.join(" / ") },
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join("、") },
    { label: "地区", value: item.region },
    { label: "年份", value: String(item.year) },
    { label: "时长", value: item.duration },
  ];

  return (
    <>
      <section className="relative w-full">
        <div className="relative h-[320px] w-full overflow-hidden md:h-[460px]">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title}-榴莲视频`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/80 to-background-50/40"></div>
        </div>

        <div className="mx-auto -mt-40 max-w-[1280px] px-4 md:-mt-56 md:px-8">
          <nav className="mb-4 flex flex-wrap items-center gap-2 text-xs text-foreground-400">
            <Link to="/" className="hover:text-accent-500 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={typePaths[item.type]} className="hover:text-accent-500 cursor-pointer">
              {typeLabels[item.type]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{item.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:items-end md:gap-8">
            <div className="w-[180px] shrink-0 self-start overflow-hidden rounded-lg border border-background-300/40 bg-background-200 md:w-[220px]">
              <img
                src={item.poster}
                alt={`${item.title} 海报`}
                title={`${item.title}-${typeLabels[item.type]}-榴莲视频`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs text-background-50">
                  {typeLabels[item.type]}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-accent-500/15 px-2.5 py-1 text-xs text-accent-500">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)}
                </span>
                {item.tags.map((t) => (
                  <span key={t} className="rounded-md bg-secondary-900/25 px-2.5 py-1 text-xs text-secondary-900">
                    {t}
                  </span>
                ))}
              </div>

              <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
                {item.title}
              </h1>
              <p className="mt-1 font-heading text-sm tracking-wide text-foreground-400 md:text-base">
                {item.originalTitle}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="flex items-center gap-2 rounded-md bg-accent-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即播放
                </button>
                <span className="flex items-center gap-2 rounded-md border border-background-300/60 px-4 py-3 text-xs text-foreground-400">
                  <i className="ri-information-line text-accent-500"></i>
                  演示状态 · 播放接口未接入
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-file-text-line text-accent-500"></i>
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-400 md:text-base">
              {item.synopsis}
            </p>

            <h2 className="mt-8 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-information-line text-accent-500"></i>
              影片信息
            </h2>
            <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              {meta.map((m) => (
                <div key={m.label} className="flex gap-3 border-b border-background-200/50 pb-3">
                  <dt className="w-14 shrink-0 text-sm text-foreground-300">{m.label}</dt>
                  <dd className="text-sm text-foreground-700">{m.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200/60 bg-background-100/40 p-5">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-links-line text-accent-500"></i>
                所属分类
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {item.genres.map((g) => (
                  <span key={g} className="rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-500">
                    {g}
                  </span>
                ))}
              </div>
              <Link
                to={typePaths[item.type]}
                className="mt-4 flex items-center justify-between rounded-md bg-primary-500/15 px-4 py-3 text-sm text-primary-800 transition-colors hover:bg-primary-500/25 cursor-pointer"
              >
                查看更多{typeLabels[item.type]}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-background-200/60 bg-background-100/40 p-5">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-price-tag-3-line text-accent-500"></i>
                相关标签
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {item.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary-900/25 px-3 py-1 text-xs text-secondary-900">
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-14">
          <SectionHeading
            title="相关推荐"
            subtitle={`根据「${item.genres.join("、")}」等标签为你推荐`}
            icon="ri-magic-line"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
            {related.map((r, i) => (
              <div key={r.id} className={`animate-fade-up delay-${(i % 4) + 1}`}>
                <MediaCard item={r} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <PlayModal open={playOpen} title={item.title} onClose={() => setPlayOpen(false)} />
    </>
  );
}